import React, { useState } from 'react';
import {
    TextField,
    Button,
    Alert,
    CircularProgress,
    Grid,
    Box,
    Typography
} from '@mui/material';
import { usePermissions } from '../../hooks/usePermissions';
import { requestPasswordResetCode, confirmPasswordReset } from '../../Utils/accountApi';
import { useTranslation } from '../../hooks/useTranslation';

export default function SecurityTab() {
    const { currentUser, loading: permissionsLoading } = usePermissions();
    const t = useTranslation('Security.json');
    const tCommon = useTranslation('Common.json');

    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');

    const [sending, setSending] = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [success, setSuccess] = useState(false);
    const [error, setError] = useState(null);

    const handleSendCode = async () => {
        setSending(true);
        setError(null);
        try {
            await requestPasswordResetCode();
            setCodeSent(true);
        } catch (err) {
            console.error('Erreur lors de l\'envoi du code:', err);
            setError(err.response?.data?.error || t('security.error.send'));
        } finally {
            setSending(false);
        }
    };

    const handleConfirm = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(false);

        if (newPassword.length < 8) {
            setError(t('security.error.too_short'));
            return;
        }
        if (newPassword !== confirmNewPassword) {
            setError(t('security.error.mismatch'));
            return;
        }

        setConfirming(true);
        try {
            await confirmPasswordReset(code.trim(), newPassword);
            setSuccess(true);
            setCodeSent(false);
            setCode('');
            setNewPassword('');
            setConfirmNewPassword('');
        } catch (err) {
            console.error('Erreur lors de la confirmation du code:', err);
            setError(err.response?.data?.error || t('security.error.confirm'));
        } finally {
            setConfirming(false);
        }
    };

    if (permissionsLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress sx={{ color: '#FF7233' }} />
            </Box>
        );
    }

    if (!currentUser) {
        return (
            <Alert severity="error">
                {t('security.no_user')}
            </Alert>
        );
    }

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                {t('security.password.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('security.password.description')}
            </Typography>

            {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                    {t('security.success')}
                </Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {!codeSent ? (
                <Button
                    variant="contained"
                    onClick={handleSendCode}
                    disabled={sending}
                    sx={{
                        bgcolor: '#FF7233',
                        '&:hover': { bgcolor: '#e05a1a' }
                    }}
                >
                    {sending ? <CircularProgress size={24} /> : t('security.send_code')}
                </Button>
            ) : (
                <form onSubmit={handleConfirm}>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {t('security.code_sent')}
                    </Alert>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label={t('security.field.code')}
                                value={code}
                                onChange={(e) => setCode(e.target.value)}
                                disabled={confirming}
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} />
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="password"
                                label={t('security.field.new_password')}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                disabled={confirming}
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                type="password"
                                label={t('security.field.confirm_password')}
                                value={confirmNewPassword}
                                onChange={(e) => setConfirmNewPassword(e.target.value)}
                                disabled={confirming}
                                required
                                size="small"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                                <Button
                                    onClick={() => { setCodeSent(false); setError(null); }}
                                    disabled={confirming}
                                >
                                    {tCommon('common.cancel')}
                                </Button>
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={confirming}
                                    sx={{
                                        bgcolor: '#FF7233',
                                        '&:hover': { bgcolor: '#e05a1a' }
                                    }}
                                >
                                    {confirming ? <CircularProgress size={24} /> : t('security.confirm')}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            )}
        </Box>
    );
}
